import React from 'react'
import { useQuery } from '@apollo/client'
import {
  Button,
  Buttons,
  Link,
  PageSection as Section,
  Grid,
  VerticalSpacer,
} from '@superhi/design'

import Callout from './components/Callout'
import { BASKET } from './queries'
import Summary from './components/Summary'
import LineItem from './components/LineItem'
import { LineItems } from './styles'
import useAddLineItemsFromQuery from './useAddLineItemsFromQuery'
import ROUTE from './route'

import PageHeader from 'src/components/page-headers/PageHeader'
import Loading from 'src/components/Loading'
import NoResults from 'src/components/NoResults'
import SEO from 'src/components/SEO'
import Main from 'src/components/Main'
import Page from 'src/components/Page'
import { API } from 'src/api'
import { PageConfig } from 'src/routing/types'
import { buildPath } from 'src/routing'

const COURSES_URL = buildPath('courses-index')

const GRID_1_ROW_GAP = { small: 32, medium: 32, large: 32 }
const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: [2, 7],
  large: 8,
  xlarge: 9,
}
const GRID_1_CELL_SPAN_2: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: [2, 7],
  large: 4,
  xlarge: 3,
}

const Cart: PageConfig<unknown> = () => {
  const { data, loading } = useQuery<API.CartBasket>(BASKET, {
    fetchPolicy: 'cache-and-network',
    ssr: false,
  })

  const basket = data?.basket
  const isLoading = loading && !data?.basket
  const hasBasket = (basket?.lineItems?.length || 0) > 0

  const addLineItemsResponse = useAddLineItemsFromQuery()

  return (
    <Page>
      <SEO title="Your cart" />

      <Main>
        <PageHeader
          title="Your cart"
          subtitle={
            addLineItemsResponse.loading
              ? 'Adding items...'
              : addLineItemsResponse?.error?.message
              ? 'Error adding to cart'
              : undefined
          }
        />

        <Section noTopPadding>
          {isLoading ? (
            <Grid>
              <Grid.Cell span="full">
                <Loading noPadding />
              </Grid.Cell>
            </Grid>
          ) : hasBasket ? (
            <Grid rowGap={GRID_1_ROW_GAP}>
              <Grid.Cell span={GRID_1_CELL_SPAN_1}>
                <VerticalSpacer amount={GRID_1_ROW_GAP}>
                  <Callout />
                  <LineItems>
                    {basket?.lineItems?.map((lineItem) => (
                      <LineItem
                        key={lineItem.id}
                        id={lineItem.id}
                        type={lineItem.product.__typename}
                        slug={lineItem.product.slug}
                        title={lineItem.variant.title}
                        imageUrl={lineItem.product.image.url}
                        discount={lineItem.discount}
                        variant={lineItem.variant}
                        maxQuantity={lineItem.variant.maxQuantity}
                        price={lineItem.price}
                        quantity={lineItem.quantity}
                      />
                    ))}
                  </LineItems>
                </VerticalSpacer>
              </Grid.Cell>

              <Grid.Cell span={GRID_1_CELL_SPAN_2}>
                {basket && (basket?.lineItems?.length || 0) > 0 && (
                  <Summary
                    subtotal={basket.value.value}
                    shipping={basket.requiresShipping ? 0 : undefined}
                    discounts={basket.discounts?.map((discount) => discount.code) || []}
                    totalDiscount={basket.totalDiscount.value}
                    total={basket.total.value}
                    currencyCode={basket.subtotal.currencyCode}
                  />
                )}
              </Grid.Cell>
            </Grid>
          ) : (
            <Grid>
              <Grid.Cell span="full">
                <NoResults>Your cart is empty</NoResults>
                <Buttons align="center">
                  <Button href={COURSES_URL}>Go to courses</Button>
                </Buttons>
              </Grid.Cell>
            </Grid>
          )}
        </Section>
      </Main>
    </Page>
  )
}

Cart.route = ROUTE

export default Cart
